import { Outlet, Navigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuthStore } from "@/stores/auth-store";
import { TopBar } from "./top-bar";
import { ClassicNav } from "./classic-nav";
import { MobileHeader } from "./mobile-header";
import { CommandPalette } from "@/components/common/command-palette";
import { ToastContainer } from "@/components/toast-container";
import { ErrorBoundary } from "@/components/errors/ErrorBoundary";
import { EntitlementBanner } from "@/components/billing/entitlement-banner";
import { BillingOnlyGuard } from "@/components/billing/billing-only-guard";
import { OnboardingGuard, TeamAccessBanner } from "@/components/onboarding/onboarding-guard";
import { ArrowUp } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNewLook } from "@/hooks/use-new-look";
import { NewLookFrame } from "@/components/new-look/new-look-frame";

export function AppLayout() {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const isMobile = useIsMobile();
  const [newLook] = useNewLook();

  useEffect(() => {
    const onScroll = () => { setShowScrollTop(window.scrollY > 300); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); };
  }, []);

  // Reset on page change and scroll to top
  useEffect(() => {
    setShowScrollTop(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const scrollToTop = useCallback(() => {
    const start = window.scrollY;
    const duration = 800;
    const startTime = performance.now();
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);

    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      window.scrollTo(0, start * (1 - ease(progress)));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, []);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const page = (
    <>
      {/* Subscription state (ENG-200). The banner renders nothing for an
          active organization, and the guard redirects only a billing-only
          one — an active caller sees no change from either. */}
      <EntitlementBanner />
      <TeamAccessBanner />
      <ErrorBoundary>
        <OnboardingGuard>
          <BillingOnlyGuard>
            <Outlet />
          </BillingOnlyGuard>
        </OnboardingGuard>
      </ErrorBoundary>
    </>
  );

  if (newLook) {
    return <NewLookFrame>{page}</NewLookFrame>;
  }

  return (
    <div className="flex min-h-screen flex-col bg-background pb-safe">
      {/* Main content area — TopBar scrolls with content */}
      <main className={cn(
        "flex-1 transition-[margin] duration-300 ease-out",
        isMobile ? "px-4 pb-28 pt-[calc(env(safe-area-inset-top,0px)+3.75rem)]" : "px-16 pb-24"
      )}>
        {!isMobile && <TopBar />}
        {isMobile && <MobileHeader />}
        {page}
      </main>

      {/* Scroll to top button — floats above the dock */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 10 }}
            transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
            onClick={scrollToTop}
            className={cn(
              "fixed z-[51] h-10 w-10 rounded-full glass-card border border-white/[0.12] flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-white/[0.08] transition-colors shadow-lg",
              isMobile ? "bottom-36 left-1/2 -translate-x-1/2" : "bottom-24 left-1/2 -translate-x-1/2"
            )}
          >
            <ArrowUp className="h-4 w-4" />
          </motion.button>
        )}
      </AnimatePresence>

      <ClassicNav isMobile={isMobile} />

      <CommandPalette />
      <ToastContainer />
    </div>
  );
}
